import React from 'react';
import { Card } from 'primereact/card';
import { Timeline } from 'primereact/timeline';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Panel } from 'primereact/panel';
import { Badge } from 'primereact/badge';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

export default function ProcesoCompra() {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    // Datos del proceso de compra
    const events = [
        {
            status: 'Armado del Carrito',
            date: 'Paso 1',
            icon: 'pi pi-shopping-cart',
            color: '#9C27B0',
            description: 'Selecciona los productos que deseas comprar y agrégalos a tu carrito de compras.',
            details: [
                'Navega por nuestro catálogo de productos',
                'Compara especificaciones y precios',
                'Agrega productos a tu carrito',
                'Revisa tu selección antes de continuar'
            ]
        },
        {
            status: 'Checkout y Registro',
            date: 'Paso 2',
            icon: 'pi pi-user-edit',
            color: '#673AB7',
            description: 'Completa tu información de facturación y selecciona tu método de pago preferido.',
            details: [
                'Ingresa tus datos personales y de facturación',
                'Selecciona tu método de pago (tarjeta, transferencia, etc.)',
                'Verifica la información del pedido',
                'Confirma tu orden de compra'
            ]
        },
        {
            status: 'Validación de Pago',
            date: 'Paso 3',
            icon: 'pi pi-check-circle',
            color: '#FF9800',
            description: 'Nuestro equipo de ventas validará tu pago para procesar tu orden.',
            details: [
                'Procesamiento automático del pago',
                'Verificación por parte del equipo de ventas',
                'Confirmación de la transacción',
                'Notificación de pago aprobado'
            ]
        },
        {
            status: 'Gestión Logística',
            date: 'Paso 4',
            icon: 'pi pi-cog',
            color: '#2196F3',
            description: 'Preparamos tu producto y gestionamos todos los trámites logísticos para el envío.',
            details: [
                'Preparación y empaquetado del producto',
                'Gestión de documentación',
                'Coordinación con empresa de envíos',
                'Programación de la entrega'
            ]
        },
        {
            status: 'Envío y Entrega',
            date: 'Paso 5',
            icon: 'pi pi-send',
            color: '#4CAF50',
            description: 'Te notificaremos cuando tu producto sea enviado junto con toda la documentación.',
            details: [
                'Envío del producto a tu dirección',
                'Código de seguimiento del envío',
                'Documentación del equipo incluida',
                'Confirmación de entrega'
            ]
        }
    ];

    const customizedMarker = (item) => {
        return (
            <span className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-1" style={{ backgroundColor: item.color }}>
                <i className={item.icon}></i>
            </span>
        );
    };

    const customizedContent = (item) => {
        return (
            <Card className="mt-3 ml-3 shadow-2">
                <div className="flex flex-column">
                    <div className="flex align-items-center mb-3">
                        <Badge value={item.date} className="mr-2" severity="info"></Badge>
                        <span className="font-bold text-lg text-primary">{item.status}</span>
                    </div>
                    <p className="text-600 mb-3">{item.description}</p>
                    <ul className="list-none p-0 m-0">
                        {item.details.map((detail, index) => (
                            <li key={index} className="flex align-items-center mb-2">
                                <i className="pi pi-check text-green-500 mr-2"></i>
                                <span className="text-sm">{detail}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Card>
        );
    };

    const faqData = [
        {
            header: '¿Cuánto tiempo toma el proceso completo?',
            content: 'El proceso completo desde la compra hasta la entrega generalmente toma entre 3 a 7 días hábiles, dependiendo de tu ubicación.'
        },
        {
            header: '¿Qué métodos de pago aceptan?',
            content: 'Aceptamos tarjetas de crédito y débito (Visa, Mastercard), transferencias bancarias, y otros métodos de pago digitales. Todos los pagos son procesados de forma segura.'
        },
        {
            header: '¿Puedo modificar mi pedido después de realizarlo?',
            content: 'Puedes modificar tu pedido solo antes de que sea validado por nuestro equipo de ventas. Una vez validado, los cambios no son posibles.'
        },
        {
            header: '¿Cómo puedo rastrear mi pedido?',
            content: 'Una vez que tu producto sea enviado, recibirás un código de seguimiento por email para que puedas rastrear tu pedido en tiempo real.'
        },
        {
            header: '¿Qué incluye la documentación del equipo?',
            content: 'Incluimos factura, garantía del fabricante, manual de usuario, y cualquier certificado o documento adicional según el tipo de producto.'
        }
    ];

    return (
        <div className={'landing-page theme-' + theme}>
            <Header theme={theme} onToggleTheme={toggleTheme} />
            <div className="min-h-screen">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-primary py-4">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold mb-2">Proceso de Compra</h1>
                        <p className="text-xl opacity-90 max-w-2xl mx-auto">
                            Conoce paso a paso cómo funciona nuestro proceso de compra para que tengas la mejor experiencia
                        </p>
                    </div>
                </div>
            </div>

            <div className=" mx-auto px-4 py-4">
                {/* Proceso Timeline */}
                <Card className="mb-6 shadow-3">
                    <div className="text-center mb-6">
                        <h2 className="text-3xl font-bold text-primary mb-3">
                            <i className="pi pi-list-check mr-2"></i>
                            Pasos del Proceso
                        </h2>
                        <p className="text-600 text-lg">
                            Sigue estos 5 sencillos pasos para completar tu compra
                        </p>
                    </div>
                    
                    <Timeline 
                        value={events} 
                        align="alternate" 
                        className="customized-timeline"
                        marker={customizedMarker} 
                        content={customizedContent} 
                    />
                </Card>

                {/* Información Adicional */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Garantías y Seguridad */}
                    <Card className="shadow-2">
                        <div className="text-center mb-4">
                            <i className="pi pi-shield text-4xl text-green-500 mb-3"></i>
                            <h3 className="text-2xl font-bold text-primary">Compra Segura</h3>
                        </div>
                        <ul className="list-none p-0 m-0">
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Pagos 100% seguros y encriptados</span>
                            </li>
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Garantía oficial del fabricante</span>
                            </li>
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Soporte técnico especializado</span>
                            </li>
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Productos 100% originales</span>
                            </li>
                        </ul>
                    </Card>

                    {/* Envíos y Entregas */}
                    <Card className="shadow-2">
                        <div className="text-center mb-4">
                            <i className="pi pi-truck text-4xl text-blue-500 mb-3"></i>
                            <h3 className="text-2xl font-bold text-primary">Envíos Confiables</h3>
                        </div>
                        <ul className="list-none p-0 m-0">
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Envíos a todo el Perú</span>
                            </li>
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Empaquetado especializado</span>
                            </li>
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Seguimiento en tiempo real</span>
                            </li>
                            <li className="flex align-items-center mb-3">
                                <i className="pi pi-check-circle text-green-500 mr-3"></i>
                                <span>Entrega en la fecha programada</span>
                            </li>
                        </ul>
                    </Card>
                </div>

                {/* FAQ */}
                <Card className="mb-6 shadow-3">
                    <div className="text-center mb-6">
                        <h3 className="text-3xl font-bold text-primary mb-3">
                            <i className="pi pi-question-circle mr-2"></i>
                            Preguntas Frecuentes
                        </h3>
                        <p className="text-600 text-lg">
                            Resolvemos las dudas más comunes sobre nuestro proceso de compra
                        </p>
                    </div>
                    
                    <Accordion multiple activeIndex={[0]}>
                        {faqData.map((faq, index) => (
                            <AccordionTab key={index} header={faq.header}>
                                <p className="text-600 line-height-3">{faq.content}</p>
                            </AccordionTab>
                        ))}
                    </Accordion>
                </Card>

                {/* Call to Action */}
                <Card className="text-center shadow-3">
                    <div className="py-4">
                        <h3 className="text-2xl font-bold text-primary mb-3">
                            ¿Listo para comenzar tu compra?
                        </h3>
                        <p className="text-600 mb-4 text-lg">
                            Explora nuestro catálogo y encuentra el equipo perfecto para ti
                        </p>
                        <div className="flex flex-column sm:flex-row gap-3 justify-content-center">
                            <Button 
                                label="Ver Catálogo" 
                                icon="pi pi-shopping-bag" 
                                className="p-button-lg"
                                onClick={() => navigate('/catalogo')}
                            />
                            <Button 
                                label="Contactar Soporte" 
                                icon="pi pi-phone" 
                                outlined 
                                className="p-button-lg"
                                onClick={() => window.open('https://wa.me/51978326796', '_blank')}
                            />
                        </div>
                    </div>
                </Card>
            </div>
            <Footer />
        </div></div>
    );
}